export const API_SUCCESS = "API_SUCCESS";
export const API_CHECK_TIME = 5 * 60 * 1000; // five minutes

/**
 * SERVER SIDE Types
 */

export enum KAIResponseType {
    Fast = "fast",
    Deep = "deep",
}

export enum ChatEngine {
    Standard = "standard",
    Experimental = "experimental",
}

// Response model for the GetMessageInsights endpoint
export type MessageInsightsResult = {
    dailyRefusalCounts: DailyRefusalCount[];
    detailedInsights: MessageInsightDetail[];
};

// Model for daily refusal aggregates
export type DailyRefusalCount = {
    date: Date;
    totalMessages: number;
    refusalCount: number;
    refusalPercentage: number;
};

// Model for detailed message insights
export type MessageInsightDetail = {
    id: number;
    threadId: string;
    brukerKey: string;
    environment: string;
    refusal: boolean;
    refusalReason: RefusalReason | null;
    timestamp: Date;
    position: number;
};

export enum RefusalReason {
    None = 0,
    Filtered = 1,
    ContextLengthExceeded = 2,
    RateLimitExceeded = 3,
    NoRetrievalResults = 4,
    CannotAnswer = 5,
}

export enum FeedbackCategory {
    Incorrect = 0,
    DidNotFollowInstructions = 1,
    PoorStructure = 2,
    PoorSources = 3,
    Other = 4,
    Outdated = 5,
}

export enum LanguageEnum {
    Norsk = 1,
    Engelsk = 2,
    Svensk = 3,
    Dansk = 4,
}

export enum LanguageCode {
    Norwegian = "nb",
    English = "en",
    Swedish = "sv",
    Danish = "da",
}

export function getLangCodeFromEnum(lang?: LanguageEnum): string {
    switch (lang) {
        case LanguageEnum.Norsk:
            return LanguageCode.Norwegian;
        case LanguageEnum.Engelsk:
            return LanguageCode.English;
        case LanguageEnum.Svensk:
            return LanguageCode.Swedish;
        case LanguageEnum.Dansk:
            return LanguageCode.Danish;
        default:
            console.warn(`Unexpected LanguageEnum value: ${lang}`);
            return LanguageCode.Norwegian;
    }
}

/**
 * API language codes used by the backend for AI response language preference.
 * These are 2-letter uppercase codes: NO, EN, DK, SE
 */
export enum ApiLanguageCode {
    Norwegian = "NO",
    English = "EN",
    Danish = "DK",
    Swedish = "SE",
}

/**
 * Maps i18n language codes (nb, en, da, sv) to API language codes (NO, EN, DK, SE)
 */
export function getApiLanguageCode(i18nCode: string): ApiLanguageCode {
    switch (i18nCode) {
        case LanguageCode.Norwegian:
            return ApiLanguageCode.Norwegian;
        case LanguageCode.English:
            return ApiLanguageCode.English;
        case LanguageCode.Danish:
            return ApiLanguageCode.Danish;
        case LanguageCode.Swedish:
            return ApiLanguageCode.Swedish;
        default:
            return ApiLanguageCode.Norwegian;
    }
}

/**
 * Maps API language codes (NO, EN, DK, SE) to i18n language codes (nb, en, da, sv)
 */
export function getI18nLanguageCode(apiCode: string | null): string {
    switch (apiCode?.toUpperCase()) {
        case ApiLanguageCode.Norwegian:
            return LanguageCode.Norwegian;
        case ApiLanguageCode.English:
            return LanguageCode.English;
        case ApiLanguageCode.Danish:
            return LanguageCode.Danish;
        case ApiLanguageCode.Swedish:
            return LanguageCode.Swedish;
        default:
            return LanguageCode.Norwegian;
    }
}

export enum AiStreamEventType {
    /* ThreadId, */
    Thread = 0,
    Process = 1,
    Source = 2,
    Data = 3,
    Error = 4,
    ThinkingComplete = 5,
    Completion = 6,
    /// <summary>
    /// Replaces Completion event
    /// </summary>
    StreamedChatMessage = 7,
    FollowUp = 8,
    Done = 9,
    Usage = 10,
    CompletionV2 = 11,
    ThreadName = 12,
    ThreadCreated = 13,
    AgentActivity = 14,
    AgentThought = 15,
    MessageSegment = 16,
    PluginActivity = 17,
    LowScoringAnswer = 18,
    NoAnswer = 19,
    NoSourcesAnswer = 20,
    RelatedTools = 21,
    StreamingReasoning = 22,
    Artifact = 23,
    StreamedArtifact = 24,
}

export enum RAGMode {
    DIB = 0,
    Konto = 1,
    Kostra = 2,
    IFRS = 3,
    ESG = 4,
    Kommune = 5,
}

export enum ChatStopReason {
    Stop,
    Length,
    ContentFilter,
    ToolCalls,
    FunctionCall,
    // The backend supplies the numbered members. The client sets the string
    // members.
    UnknownError = "unknown-error",
    NetworkError = "network-error",
    // The stream stopped without a stop frame, so the backend did not tell us
    // how the turn ended.
    Incomplete = "incomplete",
    // The user stopped the turn.
    Aborted = "aborted",
}

export enum StreamErrorType {
    ContextLengthExceeded,
    Unknown,
    ServiceUnavailable,
    NetworkError,
    RateLimitExceeded,
    ContentFilter,
}

export interface ThreadInfo {
    threadId: string;
    name: string;
    lastUpdated: string; // ISO date string
    messageCount: number;
}

export interface ThreadSearchResultRaw {
    threadId: string;
    threadName: string;
    matchingMessage: string;
    role: string;
    lastUpdated: string; // ISO date string
}

export interface ThreadSearchResult {
    threadId: string;
    threadName: string;
    matchingMessage: {
        content: string;
        citations: number[];
    };
    role: string;
    lastUpdated: string; // ISO date string
}

// Request types
export interface AddMessageRequest {
    message: string;
    user: {
        brukerkey: string;
    };
}

export interface RegenerateRequest {
    messagePosition: number;
    modifiedUserQuery?: string;
    user: {
        brukerkey: string;
    };
}

export interface RateMessageRequest {
    messagePosition: number;
    rating: number;
    comment?: string;
    category: FeedbackCategory | null;
}

export interface ThreadWithMetadata {
    threadId: string;
    name: string;
    messages: MessageWithMetadata[];
    lastUpdated: string; // ISO datetime string
    regenerationCount: number;
    postSearch?: ThreadPostSearch | null;
}

export interface ThreadPostSearch {
    query: string;
    tools: ListSource[];
    otherSources: ListSource[];
    createdAt?: string;
}

/**
 * One agent thought. The live SSE frame and the reloaded thread DTO name the
 * same values differently, so read the kind as `thoughtType ?? type`.
 */
export interface AgentThought {
    /** SSE frame only. */
    thoughtId?: string;
    /** Reloaded thread DTO only. Index of the thought in the message. */
    id?: number;
    content: string;
    /** SSE frame only. Free text, for example "Progress" or "reflection". */
    thoughtType?: string;
    /** Reloaded thread DTO only. Same values as `thoughtType`. */
    type?: string;
    sequenceNumber: number;
    agentName: string;
    timestamp: number;
    /**
     * The activity that produced the thought. One research operation keeps one
     * id, and a thought with no parent activity carries an empty string.
     */
    parentActivityId?: string;
}

/**
 * A status sentence that KAI sends while it works, in the language of the
 * question. The field is `text` and not `content`, because a client reads a
 * payload with `content` as answer text.
 */
export interface Narration {
    narrationId: string;
    text: string;
    /** One order for narrations, thoughts and activities together. */
    sequenceNumber: number;
    timestamp: number;
}

/** The values mirror the backend literals. Keep the mixed casing. */
export enum ThoughtType {
    Reflection = "reflection",
    /** Carries the research reasoning stream. */
    Progress = "Progress",
}

export interface AgentActivity {
    activityId: string;
    agentName: string;
    activityType: string;
    status: string;
    timestamp: number;
    sequenceNumber: number;
    metadata: Record<string, string>;
    type: number;
}
export interface PluginActivity {
    pluginName: string;
    activityType: string;
    sequenceNumber: number;
    timestamp: number;
    metadata: Record<string, string>;
}

export interface Artifact {
    id: string;
    type: string;
    schemaVersion: string;
    source: "ai" | "system" | "user";
    payload: Record<string, unknown>;
    createdAt: string;
}

export interface MessageWithMetadata {
    position: number;
    role: string; // 'user', 'assistant', 'system'
    content: string;
    sources: SourceInfo[];
    relatedTools: RelatedSourceInfo[];
    consideredSources?: SourceInfo[];
    thoughts: AgentThought[];
    narrations?: Narration[];
    activities: AgentActivity[];
    pluginActivities: PluginActivity[];
    artifacts?: Artifact[];
    rating?: number;
    category?: FeedbackCategory;
    feedback?: string;
    isRegenerated: boolean;
    timestamp: string; // ISO datetime string
    responseType?: KAIResponseType;
}

export interface SourceInfo {
    index: number;
    id: string;
    name: string;
    segmentName: string;
    segmentId: string;
    url: string;
    type: string;
    description: string;
}

export interface RelatedSourceInfo {
    index: number;
    id: string;
    title: string;
    url: string;
    type: string;
    description: string;
    score: number;
    isFile: boolean;
    updatedAt: string;
}

/**
 * END SERVER SIDE ENUMS
 */

export function isReferenceNode(json: unknown): json is string {
    if (typeof json === "string") {
        return json.includes("references");
    }

    if (Array.isArray(json)) {
        return json.some((item) => item.includes("references"));
    }

    return false;
}

export interface TestSuiteEvaluation {
    createdAt: number;
    testsuiteId: string;
    id: string;
    config: {
        providers: string[];
        prompts: Array<{
            raw: string;
            label: string;
        }>;
        tests: Array<{
            description: string;
            vars: Record<string, string>;
            assert: Array<{
                type: string;
                value: string;
            }>;
        }>;
    };
    results: Array<{
        id: string;
        evalId: string;
        promptIdx: number;
        testIdx: number;
        testCase: {
            description: string;
            vars: Record<string, string>;
            assert: Array<{
                type: string;
                value: string;
            }>;
            options: Record<string, unknown>;
            metadata: Record<string, unknown>;
        };
        prompt: {
            raw: string;
            label: string;
        };
        promptId: string;
        error: string;
        score: number;
        success: boolean;
        response: {
            output: string;
            tokenUsage: {
                total: number;
                prompt: number;
                completion: number;
            };
            cached: boolean;
            cost: number;
        };
        gradingResult: {
            pass: boolean;
            score: number;
            reason: string;
            namedScores: Record<string, unknown>;
            tokensUsed: {
                total: number;
                prompt: number;
                completion: number;
                cached: number;
            };
            componentResults: Array<{
                pass: boolean;
                score: number;
                reason: string;
                assertion: {
                    type: string;
                    value: string;
                };
            }>;
            assertion: null | {
                type: string;
                value: string;
            };
        };
        namedScores: Record<string, unknown>;
        provider: {
            id: string;
            label: string;
            config: Record<string, unknown>;
        };
        latencyMs: number;
        cost: number;
        metadata: Record<string, unknown>;
        persisted: boolean;
    }>;
    prompts: Array<{
        raw: string;
        label: string;
        id: string;
        provider: string;
        metrics: {
            score: number;
            testPassCount: number;
            testFailCount: number;
            assertPassCount: number;
            assertFailCount: number;
            totalLatencyMs: number;
            tokenUsage: {
                total: number;
                prompt: number;
                completion: number;
                cached: number;
            };
            namedScores: Record<string, unknown>;
            namedScoresCount: Record<string, unknown>;
            cost: number;
        };
    }>;
    persisted: boolean;
}

export interface EvaluateTestSuite {
    providers: string[]; // Valid provider name (e.g. openai:gpt-4o-mini)
    prompts: string[]; // List of prompts
    tests: string | TestCase[]; // Path to a CSV file, or list of test cases

    defaultTest?: Omit<TestCase, "description">; // Optional: add default vars and assertions on test case
    outputPath?: string | string[]; // Optional: write results to file
}

export interface TestCase {
    // Optional description of what you're testing
    description?: string;

    // Key-value pairs to substitute in the prompt
    vars?: Record<string, string | string[] | object>;

    // Optional list of automatic checks to run on the LLM output
    assert?: Assertion[];

    // Additional configuration settings for the prompt
    // options?: PromptConfig & OutputConfig & GradingConfig;

    // The required score for this test case.  If not provided, the test case is graded pass/fail.
    threshold?: number;

    // Override the provider for this test
    provider?: string;
}

export interface Assertion {
    type: string;
    value?: string;
    threshold?: number; // Required score for pass
    weight?: number; // The weight of this assertion compared to other assertions in the test case. Defaults to 1.
    // provider?: ApiProvider; // For assertions that require an LLM provider
}

export type AssertionType =
    | "answer-relevance"
    | "classifier"
    | "contains-all"
    | "contains-any"
    | "contains-json"
    | "contains-sql"
    | "contains-xml"
    | "contains"
    | "context-faithfulness"
    | "context-recall"
    | "context-relevance"
    | "cost"
    | "equals"
    | "factuality"
    | "human"
    | "icontains-all"
    | "icontains-any"
    | "icontains"
    | "is-json"
    | "is-sql"
    | "is-valid-openai-function-call"
    | "is-valid-openai-tools-call"
    | "is-xml"
    | "javascript"
    | "latency"
    | "levenshtein"
    | "llm-rubric"
    | "model-graded-closedqa"
    | "model-graded-factuality"
    | "moderation"
    | "perplexity-score"
    | "perplexity"
    | "python"
    | "regex"
    | "rouge-l"
    | "rouge-n"
    | "rouge-s"
    | "select-best"
    | "similar"
    | "starts-with"
    | "webhook";

export interface PagedRequest {
    pageNumber: number;
    pageSize: number;
}

export interface RedisPagedRequest {
    page: number;
    pageSize: number;
}

export interface PaginatedResponse<T> {
    items: T[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

export interface AITest {
    meta: {
        id: string;
        name: string;
        description: string;
        createdAt: string; // ISO date string
        updatedAt: string; // ISO date string
    };
    vars: TestVariable[];
    asserts: TestAssert[];
}

export interface TestVariable {
    id: string;
    key: string;
    name: string;
    value: string;
}

export interface TestAssert {
    id: string;
    key: string;
    type: string;
    value: string;
    weight: number;
    threshold?: number; // Optional because it can be null
}

export interface TestSuite {
    meta: {
        id?: string;
        name: string;
        description: string;
        createdAt?: string;
        updatedAt?: string;
    };
    providers: string[];
    prompts: string[];
    tests: string[];
}

export type DebugPrompt = {
    id: string;
    name: string;
    description: string;
    value: string;
};

export type DebugProvider = {
    label: string;
    value: string;
};

export type FilteredContentMessage = {
    error: {
        type: string;
        message: string;
    };
};

export type ThreadIdMessage = {
    threadId: string;
    type: AiStreamEventType.ThreadCreated;
};

export type ThreadMetaMessage = {
    threadId: string;
    name: string;
    datestamp: Date;
    messages: Message[];
};

export type ThreadNameMessage = {
    threadId: string;
    threadName: string;
};

export type MessageSegment = {
    segmentId: string;
    messageId: string;
    agentName: string;
    segmentType: string;
    content: string;
    order: number;
    timestamp: number;
};

export type ChatCompletionMessage = {
    id: string;
    created: number;
    model: string;
    object: string;
    system_fingerprint: string;
    choices: {
        content_filter_results: {
            hate: {
                filtered: boolean;
                severity: string;
            };
            self_harm: {
                filtered: boolean;
                severity: string;
            };
            sexual: {
                filtered: boolean;
                severity: string;
            };
            violence: {
                filtered: boolean;
                severity: string;
            };
        };
        delta: {
            content: string;
        };
        finish_reason: string | null;
        index: number;
        logprobs: null;
    }[];
};

/***** CHAT TYPES *****/

export type DatamodulTilgang = {
    id: 1;
    navn: "Datamodul";
    produkter: {id: number; navn: string}[];
};

export enum FunksjonalitetTilgangId {
    Dflow = 66,
    KAI = 85,
    KAI_Plus = 88,
    Kontohjelp = 51,
    KOSTRA_Kontoplan = 58,
}

export type FunksjonalitetTilgang = {
    id: 2;
    navn: "Funksjonalitet";
    produkter: {id: FunksjonalitetTilgangId; navn: string}[];
};

export type BokpakkeTilgang = {
    id: 3;
    navn: "Bokpakke";
    produkter: {id: number; navn: string}[];
};

export type Tilgang =
    | DatamodulTilgang
    | FunksjonalitetTilgang
    | BokpakkeTilgang;

export interface BffUser {
    session_id: string;
    kundeId: number;
    kundeNavn: string;
    kontoId: number;
    kontoNavn: string;
    epost: string;
    navn: string;
    rolleId: UserRole;
    rolle: string;
    rettighet: number;
    brukerKey: string;
    spraakId: number;
    kundegruppe: string;
    produktpakke: string;
    logonDatoTidspunkt: string;
    aktiv: boolean;
    opprettet: string;
    lisensTil: string | null;
    demokonto: boolean;
    applicationId: number;
    krevSSO: boolean;
    epostSuffiks: string;
    initials: string;
    tilganger: Tilgang[];
}

export enum UserRole {
    Kunde = 1,
    KundePriviligert = 2,
    Fagarbeider = 3,
    Administrator = 4,
    Superbruker = 5,
    Kundeadmin = 6,
    Selger = 7,
}

export const HasNoAdminPrivileges = [
    UserRole.Kunde,
    UserRole.KundePriviligert,
    UserRole.Selger,
];

export const HasAdminPrivileges = [UserRole.Superbruker];

export const HasEditorPrivileges = [
    UserRole.Fagarbeider,
    UserRole.Administrator,
    UserRole.Superbruker,
];

export interface Message {
    id?: string;
    role: "system" | "user" | "assistant";
    ragProcesses?: RagProcessMessage[];
    ragSources?: RagSourceMessage[];
    datestamp?: Date;
    ragProcessStart?: Date;
    ragProcessEnd?: Date;
    followUpQuestions?: string[];
    usage: Record<GPTModel, Usage>;
    stopReason?: ChatStopReason;
    error?: StreamErrorMessage;
}

export interface UserMessage extends Message {
    role: "user";
    content: string;
}

export interface SystemMessage extends Message {
    role: "system";
    content: string;
}

export interface AssistantContentReference {
    id: string;
    content: string;
    href: string;
}

export interface AssistantAnswer {
    content: string;
    citations?: number[];
}

export interface AssistantReasoningContent {
    thinking: string[];
    steps: string[];
    reflection: string;
    answer: AssistantAnswer;
}
export interface AssistantMessage extends Omit<Message, "content"> {
    role: "assistant";
    content: string;
}

export const isAssistantMessage = (message: MessageWithMetadata) => {
    return message.role === "assistant";
};

export const isUserMessage = (message: MessageWithMetadata) => {
    return message.role === "user";
};

export const isSystemMessage = (message: MessageWithMetadata) => {
    return message.role === "system";
};
export type GPTModel = "gpt-4o" | "gpt-4o-mini";

export interface Usage {
    completion_tokens: number;
    prompt_tokens: number;
    total_tokens: number;
}

export interface RagProcessMessage {
    language: string;
    message: string;
    service: string;
    tag: string;
}

export interface Pipeline {
    description: string;
    model: string;
    name: string;
    promptid: string;
    tag: string;
    outputformat: string;
}

export interface RagSourceMessage {
    index: number;
    title: string;
    topictype: string;
    topicid: string;
    resourceid: string;
    segmentId?: string;
    segmentName: string;
    bookmark?: string;
    foundin?: string;
    url?: string;
}

export interface RelatedToolsMessage {
    index: number;
    title: string;
    topictype: string;
    topicid: string;
    url: string;
}

export interface ThinkingCompleteMessage {
    ragStart: number;
    ragEnd: number;
}

export interface FollowUpMessage {
    questions: string[];
}

export interface UsageMessage {
    model: GPTModel;
    completion_tokens: number;
    prompt_tokens: number;
    total_tokens: number;
}

export interface StopMessage {
    finished: {
        HasValue: boolean;
        Value: unknown;
    };
}

export interface StreamErrorMessage {
    errorType: StreamErrorType;
    message: string;
}

export type System = {
    name: string;
    queryAnalysis?: {
        pipeline: Pipeline[];
    };
    retrieval?: {
        pipeline: Pipeline[];
        searchindex: string;
    };
    completion?: {
        pipeline: Pipeline[];
    };
};

export interface LowScoreMessage {
    type: AiStreamEventType.LowScoringAnswer;
    score: number;
    messageId: number;
}

export interface NoAnswerMessage {
    type: AiStreamEventType.NoAnswer;
    messageId: number;
}

export interface NoSourcesMessage {
    type: AiStreamEventType.NoSourcesAnswer;
    messageId: number;
}

export interface ArtifactMessage {
    type: AiStreamEventType.Artifact;
    id: string;
    artifactType: string;
    schemaVersion: string;
    llmSummary: string;
    payload: Record<string, unknown>;
}

export interface StreamedArtifactMessage {
    type: AiStreamEventType.StreamedArtifact;
    id: string;
    artifactType?: string; // present on start message
    schemaVersion?: string; // present on start message
    delta?: string; // present on delta messages
}

export interface Thread {
    id: string;
    name: string;
    system: System;
    datestamp: Date;
    messages: Message[];
}

export const isFilteredContent = (
    obj: unknown
): obj is FilteredContentMessage => {
    const requiredProps = ["type", "message"];
    if (typeof obj === "object" && obj !== null && "error" in obj) {
        return requiredProps.every(
            (prop) => prop in (obj as FilteredContentMessage).error
        );
    }
    return false;
};
export const isFilteredContentish = (str: unknown): boolean => {
    return typeof str === "string" && str.includes("content_filtered");
};

export type MessageType =
    | ThreadMetaMessage
    | RagProcessMessage
    | RagSourceMessage
    | ChatCompletionMessage
    | MessageSegment
    | FilteredContentMessage
    | ThinkingCompleteMessage
    | FollowUpMessage
    | UsageMessage
    | StopMessage
    | StreamErrorMessage
    | LowScoreMessage
    | NoAnswerMessage
    | NoSourcesMessage;

export enum MessageEnum {
    ThreadCreated,
    ThreadMetaMessage,
    ThreadNameMessage,
    RagProcessMessage,
    RagSourceMessage,
    RelatedToolsMessage,
    ChatCompletionMessage,
    FilteredContentMessage,
    ThinkingCompleteMessage,
    FollowUpMessage,
    UsageMessage,
    StopMessage,
    ErrorMessage,
    AgentActivity,
    PluginActivity,
    AgentThought,
    MessageSegment,
    LowScoreMessage,
    NoAnswerMessage,
    NoScoreMessage,
    ArtifactMessage,
    StreamedArtifactMessage,
    PostSearchResultMessage,
    PostSearchStartMessage,
    Narration,
}

// Guards
export const isThreadCreatedMessage = (
    obj: unknown
): obj is ThreadIdMessage => {
    return (
        typeof obj === "object" &&
        obj !== null &&
        "type" in obj &&
        obj.type === AiStreamEventType.ThreadCreated
    );
};
export const isThreadMetaMessage = (obj: unknown): obj is ThreadMetaMessage => {
    return (
        typeof obj === "object" &&
        obj !== null &&
        "threadId" in obj &&
        "messages" in obj
    );
};

export const isThreadNameMessage = (obj: unknown): obj is ThreadNameMessage => {
    return (
        typeof obj === "object" &&
        obj !== null &&
        "type" in obj &&
        obj.type === AiStreamEventType.ThreadName
    );
};

export const isRagProcessMessage = (obj: unknown): obj is RagProcessMessage => {
    const requiredProps = ["language", "message", "service", "tag"];
    return (
        typeof obj === "object" &&
        obj !== null &&
        requiredProps.every((prop) => prop in obj)
    );
};

// SSE `process` frame with tag "postsearch" — emitted when the backend starts
// an in-stream PostSearch on the first turn of a topical question. Start-only:
// there is no "finished" frame, so the loading state is cleared by the
// `postsearchresult` event or the stream end (whichever comes first).
export const isPostSearchStartMessage = (
    obj: unknown
): obj is RagProcessMessage =>
    isRagProcessMessage(obj) && obj.tag === "postsearch";

export const isRagSourceMessage = (obj: unknown): obj is RagSourceMessage => {
    const requiredProps = ["title", "topictype", "topicid", "resourceid"];
    return (
        typeof obj === "object" &&
        obj !== null &&
        requiredProps.every((prop) => prop in obj)
    );
};

export const isRelatedToolsMessage = (
    obj: unknown
): obj is RagSourceMessage => {
    const requiredProps = ["title", "topictype", "topicid", "url"];
    return (
        typeof obj === "object" &&
        obj !== null &&
        requiredProps.every((prop) => prop in obj)
    );
};

export const isChatCompletionMessage = (
    obj: unknown
): obj is ChatCompletionMessage => {
    return typeof obj === "object" && obj !== null && "content" in obj;
};
export const isMessageSegment = (obj: unknown): obj is MessageSegment => {
    return (
        typeof obj === "object" &&
        obj !== null &&
        "segmentType" in obj &&
        obj.segmentType === "content"
    );
};

export const isFilteredContentMessage = (
    obj: unknown
): obj is FilteredContentMessage => {
    return typeof obj === "object" && obj !== null && "filtered" in obj;
};

export const isThinkingCompleteMessage = (
    obj: unknown
): obj is ThinkingCompleteMessage => {
    return (
        typeof obj === "object" &&
        obj !== null &&
        "ragStart" in obj &&
        "ragEnd" in obj
    );
};

export const isAgentActivity = (obj: object): obj is AgentActivity => {
    return "agentName" in obj && "activityType" in obj;
};

export const isPluginActivity = (obj: object): obj is PluginActivity => {
    return "pluginName" in obj;
};

export const isAgentThought = (obj: object): obj is AgentThought => {
    return "thoughtId" in obj;
};

export const isNarration = (obj: object): obj is Narration => {
    return "narrationId" in obj;
};

export const isFollowUpMessage = (obj: object): obj is FollowUpMessage => {
    return (
        "questions" in obj &&
        Array.isArray((obj as FollowUpMessage).questions) &&
        (obj as FollowUpMessage).questions.every((q) => typeof q === "string")
    );
};

export const isUsageMessage = (obj: unknown): obj is UsageMessage => {
    return typeof obj === "object" && obj !== null && "usage" in obj;
};

//data: {"Completion":" dess","IsDone":false,"Type":10}
export const isStopMessage = (obj: unknown): obj is string => {
    return (
        typeof obj === "object" &&
        obj !== null &&
        "stop_reason" in obj &&
        [
            ChatStopReason.Stop,
            ChatStopReason.Length,
            ChatStopReason.ContentFilter,
        ].includes(obj.stop_reason as ChatStopReason)
    );
};

export const isErrorMessage = (obj: unknown): obj is StreamErrorMessage => {
    return (
        typeof obj === "object" &&
        obj !== null &&
        "type" in obj &&
        obj.type === AiStreamEventType.Error
    );
};

export const isLowScoreMessage = (obj: object): obj is LowScoreMessage => {
    return "type" in obj && obj.type === AiStreamEventType.LowScoringAnswer;
};
export const isNoAnswerMessage = (obj: object): obj is NoAnswerMessage => {
    return "type" in obj && obj.type === AiStreamEventType.NoAnswer;
};
export const isNoScoreMessage = (obj: object): obj is NoSourcesMessage => {
    return "type" in obj && obj.type === AiStreamEventType.NoSourcesAnswer;
};

export const isArtifactMessage = (obj: unknown): obj is ArtifactMessage => {
    return (
        typeof obj === "object" &&
        obj !== null &&
        "type" in obj &&
        obj.type === AiStreamEventType.Artifact &&
        "artifactType" in obj
    );
};

export const isPostSearchResultMessage = (
    obj: unknown
): obj is PostSearchResultMessage => {
    return (
        typeof obj === "object" &&
        obj !== null &&
        "tools" in obj &&
        "otherSources" in obj
    );
};

export const isStreamedArtifactMessage = (
    obj: unknown
): obj is StreamedArtifactMessage => {
    return (
        typeof obj === "object" &&
        obj !== null &&
        "type" in obj &&
        obj.type === AiStreamEventType.StreamedArtifact
    );
};

// Optimized message type detection with early returns
export const messageTypeToEnum = (obj: unknown): MessageEnum | null => {
    if (typeof obj !== "object" || obj === null) {
        return null;
    }

    // This guard must stay first. The guards below test one field each, and a
    // narration payload can also match one of them.
    if (isNarration(obj)) return MessageEnum.Narration;
    if (isPostSearchResultMessage(obj))
        return MessageEnum.PostSearchResultMessage;
    if (isThreadCreatedMessage(obj)) return MessageEnum.ThreadCreated;
    if (isThreadMetaMessage(obj)) return MessageEnum.ThreadMetaMessage;
    if (isThreadNameMessage(obj)) return MessageEnum.ThreadNameMessage;
    if (isAgentThought(obj)) return MessageEnum.AgentThought;
    if (isAgentActivity(obj)) return MessageEnum.AgentActivity;
    if (isPluginActivity(obj)) return MessageEnum.PluginActivity;
    if (isStreamedArtifactMessage(obj))
        return MessageEnum.StreamedArtifactMessage;
    if (isArtifactMessage(obj)) return MessageEnum.ArtifactMessage;
    if (isChatCompletionMessage(obj)) return MessageEnum.ChatCompletionMessage;
    if (isMessageSegment(obj)) return MessageEnum.MessageSegment;
    if (isStopMessage(obj)) return MessageEnum.StopMessage;
    if (isPostSearchStartMessage(obj))
        return MessageEnum.PostSearchStartMessage;
    if (isRagProcessMessage(obj)) return MessageEnum.RagProcessMessage;
    if (isRagSourceMessage(obj)) return MessageEnum.RagSourceMessage;
    if (isRelatedToolsMessage(obj)) return MessageEnum.RelatedToolsMessage;
    if (isFilteredContentMessage(obj))
        return MessageEnum.FilteredContentMessage;
    if (isThinkingCompleteMessage(obj))
        return MessageEnum.ThinkingCompleteMessage;
    if (isFollowUpMessage(obj)) return MessageEnum.FollowUpMessage;
    if (isUsageMessage(obj)) return MessageEnum.UsageMessage;
    if (isErrorMessage(obj)) return MessageEnum.ErrorMessage;
    if (isLowScoreMessage(obj)) return MessageEnum.LowScoreMessage;
    if (isNoAnswerMessage(obj)) return MessageEnum.NoAnswerMessage;
    if (isNoScoreMessage(obj)) return MessageEnum.NoScoreMessage;

    return null;
};

// Improved message types with discriminated unions
export type StreamMessage =
    | {type: "ThreadCreated"; threadId: string}
    | {
          type: "ThreadMeta";
          threadId: string;
          name: string;
          datestamp: Date;
          messages: Message[];
      }
    | {type: "ThreadName"; threadId: string; threadName: string}
    | {type: "ChatCompletion"; content: string}
    | {type: "Stop"; stop_reason: ChatStopReason}
    | {
          type: "RagProcess";
          language: string;
          message: string;
          service: string;
          tag: string;
      }
    | {
          type: "RagSource";
          index: number;
          title: string;
          topictype: string;
          topicid: string;
          resourceid: string;
      }
    | {type: "FilteredContent"; error: {type: string; message: string}}
    | {type: "ThinkingComplete"; ragStart: number; ragEnd: number}
    | {type: "FollowUp"; questions: string[]}
    | {
          type: "Usage";
          model: GPTModel;
          completion_tokens: number;
          prompt_tokens: number;
          total_tokens: number;
      }
    | {type: "Error"; errorType: StreamErrorType; message: string}
    | {
          type: "AgentActivity";
          activityId: string;
          agentName: string;
          activityType: string;
          status: string;
          timestamp: number;
      }
    | {
          type: "AgentThought";
          id: number;
          content: string;
          thoughtType: ThoughtType;
          order: number;
          agentName: string;
          timestamp: number;
      };

// Type guard for the new discriminated union
export const isStreamMessage = (obj: unknown): obj is StreamMessage => {
    return typeof obj === "object" && obj !== null && "type" in obj;
};

export enum LocalStorageKeys {
    ResponseType = "responseType",
    ResponseTypeMigrationV1 = "responseTypeMigrationV1",
    ChatEngine = "chatEngine",
    // Prefix for the per-user feature bitmask: `featureFlags:<brukerKey>`.
    // The legacy boolean flag keys live only in featureFlags.ts.
    FeatureFlags = "featureFlags",
    LangInitialized = "langInitialized",
    SidebarPosition = "context-sidebar-position",
}

export const USER_THREADS_PAGE_SIZE = 30;

export interface QuickSearchRequest {
    query: string;
    take: number;
    systemname: string;
}

export interface PostSearchRequest {
    query: string;
    take: number;
    systemname: string;
    threadId?: string | null;
}

export interface ListSource {
    score: number | null;
    topicId: string;
    segmentId: string | null;
    topicTitle: string;
    segmentTitle: string | null;
    topicType: string;
    topicTypeid: number | null;
    resourceTypeid: number | null;
    description: string | null;
    url: string;
}

export interface PostSearchData {
    keywordsGenerated: string;
    hyDeVector: string;
    searchQuery: string;
}

export interface PostSearchResponse {
    tools?: ListSource[];
    otherSources: ListSource[];
    data: PostSearchData | null;
}

// SSE `postsearchresult` event — the backend now runs PostSearch inside the
// completion stream and emits the result here on the first turn of a thread.
export interface PostSearchResultMessage {
    query: string;
    tools: ListSource[];
    otherSources: ListSource[];
}

export interface QuickSearchResult {
    topicid: string;
    segmentid: string;
    segmenttitle: string;
    topictitle: string;
    score: string;
    topictypename: string;
    topictype: string;
    topictypeid: string;
    resourcetypename: string;
    resourcetypeid: string;
    lastopened: string;
    url: string;
}

export interface TriggerSearchResult {
    score: number | null;
    topicId: string;
    segmentId: string | null;
    topicTitle: string;
    segmentTitle: string | null;
    topicType: string | null;
    topicTypeid: number | null;
    resourceTypeid: number | null;
    description: string | null;
    url: string;
}

export interface DirectSearchResultMetadata {
    topicId?: string;
    topicType?: string | null;
    topicTypeid?: number | null;
    resourceTypeid?: number | null;
    segmentId?: string | null;
}

export interface DirectSearchResult {
    id: string;
    title: string;
    snippet: string;
    // Tool-suggestion artifacts carry the item text as `description`; the trigger/post-search
    // path maps it to `snippet`. Renderers read description first, then fall back to snippet.
    description?: string;
    url: string;
    score: number;
    metadata?: DirectSearchResultMetadata;
}

export interface EphemeralSearch {
    query: string;
    results: DirectSearchResult[];
}

export interface PostSearchState {
    streamId: string;
    query: string;
    status: "searching" | "ready" | "error" | "empty";
    tools: ListSource[];
    otherSources: ListSource[];
    persisted?: boolean;
}

export interface PasteAttachment {
    type: "paste";
    content: string;
    /** Set when the paste was cut at the client-side character limit. */
    truncated?: boolean;
}

export interface PriorArtifact {
    type: string;
    payload: Record<string, unknown>;
}

export interface SeedMessage {
    role: "user" | "assistant";
    content: string;
    artifacts?: PriorArtifact[];
}

export interface UserSettingsResponse {
    preferredLanguage: string | null;
}

export interface UpdateUserSettingsRequest {
    preferredLanguage: string | null;
}

export interface InitResponse {
    threads: PaginatedResponse<ThreadInfo>;
    recentTopics: QuickSearchResult[];
    settings: UserSettingsResponse;
}
